import React, { useState, useEffect } from 'react';
import { toast } from 'react-toastify';
import config from '../../config';
import {
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
  TextField,
  Box,
  CircularProgress,
  Grid,
  MenuItem,
  Button,
  Select,
  InputLabel,
  FormControl,
  Chip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
} from '@mui/material';
import { motion } from 'framer-motion';
import FileDownloadIcon from '@mui/icons-material/FileDownload';
import * as XLSX from 'xlsx';

const company = JSON.parse(sessionStorage.getItem('companyDetails') || '{}');
const Companycurrency = company?.currency ?? '';

// Restaurant Payments Ledger — mirrors the hotel Payment Ledger screen but
// reads from the restaurant Orderledger (order payments + manual cash) via
// GET /home/GetRestaurantPayments/{mode}/{from}/{to}/{companyId}.
export default function RestaurantPayments() {
  const [report, setReport] = useState(null);
  const [loading, setLoading] = useState(true);

  // Filters
  const [mode, setMode] = useState('');
  const [fromDate, setFromDate] = useState('');
  const [toDate, setToDate] = useState('');
  const [searchText, setSearchText] = useState('');
  // CR/DR transaction-type filter (#9) - '' = All
  const [txnType, setTxnType] = useState('');
  const [editRow, setEditRow] = useState(null);
  const [savingEdit, setSavingEdit] = useState(false);

  const token = sessionStorage.getItem('token');

  const fetchReport = async (selectedMode, fDate, tDate) => {
    setLoading(true);
    try {
      const modeParam = selectedMode || 'null';
      const fromDateParam = fDate || 'null';
      const toDateParam = tDate || 'null';

      const url = `${config.API_BASE_URL}/home/GetRestaurantPayments/${modeParam}/${fromDateParam}/${toDateParam}/${company?.id}`;
      const res = await fetch(url, {
        headers: { Authorization: `Bearer ${token}` },
      });

      const text = await res.text();
      const data = text ? JSON.parse(text) : null;
      setReport(data);
    } catch (err) {
      console.error('Error fetching restaurant payments:', err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchReport(null, null, null);
  }, []);

  const handleFilterApply = () => {
    fetchReport(mode, fromDate, toDate);
  };

  const handleReset = () => {
    setMode('');
    setFromDate('');
    setToDate('');
    setSearchText('');
    setTxnType('');
    fetchReport(null, null, null);
  };

  const openEdit = (entry) => {
    setEditRow({
      id: entry.id, amount: entry.amount ?? '', mode: entry.mode || '',
      type: entry.type || '', remark: entry.remark || '', refno: entry.refno || '',
    });
  };

  const handleEditSave = async () => {
    if (!editRow) return;
    setSavingEdit(true);
    try {
      const token = sessionStorage.getItem('token');
      const res = await fetch(`${config.API_BASE_URL}/Home/UpdateRestaurantPayment`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify({
          Id: editRow.id, Amount: Number(editRow.amount) || 0, Mode: editRow.mode,
          Type: editRow.type, Remark: editRow.remark, RefNo: editRow.refno,
          Companyid: company?.id, Userid: Number(sessionStorage.getItem('userId') || 0),
        }),
      });
      if (!res.ok) throw new Error();
      toast.success('Payment updated');
      setEditRow(null);
      fetchReport(mode, fromDate, toDate);
    } catch (e) { toast.error('Failed to update payment'); }
    finally { setSavingEdit(false); }
  };

  if (loading) {
    return (
      <Box
        display='flex'
        justifyContent='center'
        alignItems='center'
        minHeight='60vh'>
        <CircularProgress size={60} />
      </Box>
    );
  }

  if (!report) {
    return (
      <Typography variant='h6' align='center' sx={{ mt: 4 }}>
        No data available
      </Typography>
    );
  }

  // Summary Cards — Cash / Card / UPI-Online / Due
  const summaryCards = [
    {
      label: 'Total Cash',
      value: report.totalCash || 0,
      color: '#388e3c',
      bg: '#e8f5e9',
    },
    {
      label: 'Total Card',
      value: report.totalCard || 0,
      color: '#1976d2',
      bg: '#e3f2fd',
    },
    {
      label: 'Total UPI / Online',
      value: (report.totalOnline || 0) + (report.totalUPI || 0),
      color: '#7b1fa2',
      bg: '#f3e5f5',
    },
    {
      label: 'Total Due',
      value: report.totalDue || 0,
      color: '#d32f2f',
      bg: '#ffebee',
    },
  ];

  const filteredLedgers =
    report.ledgers?.filter(
      (entry) =>
        (!txnType ||
          (entry.type || '').toString().toUpperCase() === txnType) &&
        Object.values(entry).some((val) =>
          val?.toString().toLowerCase().includes(searchText.toLowerCase()),
        ),
    ) || [];

  const exportToExcel = () => {
    if (!report?.ledgers?.length) return toast.info('No data to export');

    const rows = filteredLedgers.map((e) => ({
      Cashier: e.username,
      'Order ID': e.bookingId || '',
      Date: e.tranjectiondate
        ? new Date(e.tranjectiondate).toLocaleString('en-GB')
        : '',
      Mode: e.mode,
      Type: e.type,
      Amount: parseFloat(e.amount || 0).toFixed(2),
      'Card Type': e.cardtype || '',
      'Card Number': e.cardnumber || '',
      'Ref No': e.refno || '',
      Remark: e.remark || '',
    }));

    const worksheet = XLSX.utils.json_to_sheet(rows);
    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, 'RestaurantPayments');
    XLSX.writeFile(
      workbook,
      `Restaurant_Payments_${new Date().toISOString().slice(0, 10)}.xlsx`,
    );
  };

  return (
    <Box sx={{ p: 4 }}>
      <Typography
        variant='h4'
        fontWeight='bold'
        mb={3}
        textAlign='center'
        sx={{ color: '#0a2540' }}>
        🍽️ Restaurant Payments (Order Ledger)
      </Typography>

      {/* Summary Cards */}
      <Grid container spacing={3} mb={4}>
        {summaryCards.map((item, i) => (
          <Grid item xs={12} sm={6} md={3} key={i}>
            <motion.div whileHover={{ scale: 1.05 }}>
              <Paper
                elevation={4}
                sx={{
                  borderRadius: 3,
                  p: 3,
                  textAlign: 'center',
                  background: item.bg,
                }}>
                <Typography variant='body2' color='text.secondary'>
                  {item.label}
                </Typography>
                <Typography
                  variant='h5'
                  fontWeight='bold'
                  sx={{ color: item.color, mt: 1 }}>
                  {Companycurrency} {Number(item.value).toFixed(2)}
                </Typography>
              </Paper>
            </motion.div>
          </Grid>
        ))}
      </Grid>

      {/* Filters */}
      <Box display='flex' gap={2} mb={3} flexWrap='wrap' alignItems='center'>
        <FormControl size='small' sx={{ minWidth: 150 }}>
          <InputLabel>Mode</InputLabel>
          <Select
            value={mode}
            label='Mode'
            onChange={(e) => setMode(e.target.value)}>
            <MenuItem value=''>All</MenuItem>
            <MenuItem value='cash'>Cash</MenuItem>
            <MenuItem value='card'>Card</MenuItem>
            <MenuItem value='online'>UPI / Online</MenuItem>
            <MenuItem value='due'>Due</MenuItem>
          </Select>
        </FormControl>

        <FormControl size='small' sx={{ minWidth: 130 }}>
          <InputLabel>Type</InputLabel>
          <Select
            value={txnType}
            label='Type'
            onChange={(e) => setTxnType(e.target.value)}>
            <MenuItem value=''>All</MenuItem>
            <MenuItem value='CR'>CR</MenuItem>
            <MenuItem value='DR'>DR</MenuItem>
          </Select>
        </FormControl>

        <TextField
          label='From Date'
          type='date'
          size='small'
          value={fromDate}
          onChange={(e) => setFromDate(e.target.value)}
          InputLabelProps={{ shrink: true }}
        />

        <TextField
          label='To Date'
          type='date'
          size='small'
          value={toDate}
          onChange={(e) => setToDate(e.target.value)}
          InputLabelProps={{ shrink: true }}
        />

        <TextField
          label='🔍 Search (any field)'
          size='small'
          value={searchText}
          onChange={(e) => setSearchText(e.target.value)}
        />

        <Button variant='contained' onClick={handleFilterApply}>
          Apply Filter
        </Button>

        <Button variant='outlined' onClick={handleReset}>
          Reset
        </Button>

        <Button
          variant='contained'
          color='secondary'
          startIcon={<FileDownloadIcon />}
          onClick={exportToExcel}>
          Export Excel
        </Button>
      </Box>

      {/* Table */}
      <Paper sx={{ borderRadius: 3 }}>
        <TableContainer sx={{ maxHeight: 600 }}>
          <Table stickyHeader>
            <TableHead>
              <TableRow>
                {[
                  '#',
                  'Cashier',
                  'Order ID',
                  'Date',
                  'Mode',
                  'Type',
                  `Amount (${Companycurrency})`,
                  'Card Type',
                  'Card Number',
                  'Ref No',
                  'Remark',
                  'Action',
                ].map((head, i) => (
                  <TableCell
                    key={i}
                    sx={{
                      backgroundColor: '#0a2540',
                      color: '#fff',
                      fontWeight: 'bold',
                    }}>
                    {head}
                  </TableCell>
                ))}
              </TableRow>
            </TableHead>
            <TableBody>
              {filteredLedgers.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={12} align='center'>
                    No records found
                  </TableCell>
                </TableRow>
              ) : (
                filteredLedgers.map((entry, i) => (
                  <TableRow key={entry.id || i}>
                    <TableCell>{i + 1}</TableCell>
                    <TableCell>{entry.username}</TableCell>
                    <TableCell sx={{ fontWeight: 'bold', color: '#1976d2' }}>
                      {entry.bookingId ? `#${entry.bookingId}` : '-'}
                    </TableCell>
                    <TableCell>
                      {entry.tranjectiondate
                        ? new Date(entry.tranjectiondate).toLocaleString('en-GB')
                        : '-'}
                    </TableCell>
                    <TableCell sx={{ textTransform: 'capitalize' }}>
                      {entry.mode}
                    </TableCell>
                    <TableCell>
                      <Chip
                        label={entry.type || '-'}
                        size='small'
                        color={entry.type === 'CR' ? 'success' : 'error'}
                        variant='outlined'
                      />
                    </TableCell>
                    <TableCell sx={{ fontWeight: 'bold' }}>
                      {Companycurrency}{' '}
                      {parseFloat(entry.amount || 0).toFixed(2)}
                    </TableCell>
                    <TableCell>{entry.cardtype || '-'}</TableCell>
                    <TableCell>{entry.cardnumber || '-'}</TableCell>
                    <TableCell>{entry.refno || '-'}</TableCell>
                    <TableCell>{entry.remark || '-'}</TableCell>
                    <TableCell>
                      <Button size='small' variant='outlined' onClick={() => openEdit(entry)}>Edit</Button>
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </TableContainer>
      </Paper>

      <Dialog open={!!editRow} onClose={() => setEditRow(null)} maxWidth='xs' fullWidth>
        <DialogTitle>Edit Payment</DialogTitle>
        <DialogContent dividers>
          {editRow && (
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 0.5 }}>
              <FormControl size='small' fullWidth>
                <InputLabel>Type</InputLabel>
                <Select value={editRow.type} label='Type' onChange={(e) => setEditRow({ ...editRow, type: e.target.value })}>
                  <MenuItem value='CR'>CR</MenuItem>
                  <MenuItem value='DR'>DR</MenuItem>
                </Select>
              </FormControl>
              <FormControl size='small' fullWidth>
                <InputLabel>Mode</InputLabel>
                <Select value={editRow.mode} label='Mode' onChange={(e) => setEditRow({ ...editRow, mode: e.target.value })}>
                  <MenuItem value='cash'>Cash</MenuItem>
                  <MenuItem value='card'>Card</MenuItem>
                  <MenuItem value='upi'>UPI</MenuItem>
                  <MenuItem value='online'>Online</MenuItem>
                  <MenuItem value='due'>Due</MenuItem>
                </Select>
              </FormControl>
              <TextField size='small' label='Amount' type='number' value={editRow.amount} onChange={(e) => setEditRow({ ...editRow, amount: e.target.value })} />
              <TextField size='small' label='Remark' value={editRow.remark} onChange={(e) => setEditRow({ ...editRow, remark: e.target.value })} />
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setEditRow(null)} color='inherit'>Cancel</Button>
          <Button onClick={handleEditSave} variant='contained' disabled={savingEdit}>{savingEdit ? 'Saving...' : 'Save'}</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
